import { ErrorOutlineRounded, LoginOutlined } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useRef, useState } from "react";
import EmailInput from "../components/elements/EmailInput";
import OnboardingLoginAndRegister from "../components/elements/OnboardingLoginAndRegister";
import PasswordInput from "../components/elements/PasswordInput";
import { useLogin } from "./hooks/useLogin";

export default function LoginPage() {
  const [isValidEmail, setIsValidEmail] = useState(false);
  const [isValidDoc, setIsValidDoc] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const {
    email,
    setEmail,
    password,
    setPassword,
    code,
    setCode,
    step,
    error,
    isSubmittingEmail,
    isLogged,
    isPasswordValid,
    handleVerifyCodeByEmail,
    recaptchaContainerRef,
  } = useLogin();

  if (isLogged) window.location.href = "/dashboard";

  const loginForm = (
    <Card
      sx={{
        maxWidth: 420,
        mx: "auto",
        borderRadius: 4,
        background: "background.paper",
        boxShadow: "0 10px 40px rgba(0,0,0,0.08)",
      }}
    >
      <CardContent sx={{ p: 4 }}>
        <Stack spacing={1.5} alignItems="center" textAlign="center">
          <Box
            sx={{
              bgcolor: "primary.main",
              width: 56,
              height: 56,
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 0 20px rgba(0,0,0,0.08)",
            }}
          >
            <LoginOutlined sx={{ color: "#fff", fontSize: 30 }} />
          </Box>

          <Typography
            variant="h5"
            fontWeight={800}
            sx={{
              fontFamily: "Montserrat, sans-serif",
            }}
          >
            Iniciar sesión
          </Typography>

          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ mb: 1.5, fontWeight: 500 }}
          >
            Accede con tu correo y contraseña
          </Typography>
        </Stack>

        <form ref={formRef} onSubmit={handleVerifyCodeByEmail}>
          <Stack spacing={2} sx={{ mt: 3 }}>
            <EmailInput
              email={email}
              setEmail={setEmail}
              setIsValid={setIsValidEmail}
            />
          </Stack>
          <Stack spacing={2} sx={{ mt: 3 }}>
            <PasswordInput value={password} onChange={setPassword} />
            <Button
              fullWidth
              variant="contained"
              type="submit"
              sx={{
                py: 1.3,
                fontWeight: 600,
                borderRadius: 2,
                background: "#00e677b2",
              }}
              disabled={!isValidEmail || !isPasswordValid || isSubmittingEmail}
            >
              {isSubmittingEmail ? (
                <CircularProgress size={22} />
              ) : (
                "Verificar código"
              )}
            </Button>
          </Stack>
        </form>

        {error && (
          <Alert
            severity="error"
            sx={{
              mt: 2,
              fontWeight: 600,
              fontSize: 15,
              textAlign: "center",
            }}
            iconMapping={{
              error: <ErrorOutlineRounded sx={{ fontSize: 26 }} />,
            }}
          >
            {error}
          </Alert>
        )}
      </CardContent>
    </Card>
  );

  return (
    <Box
      sx={{
        display: "flex",
        minHeight: "100vh",
        flexDirection: { xs: "column", md: "row" },
        backgroundColor: "background.paper",
      }}
    >
      {isMobile ? (
        <Box
          sx={{
            display: { xs: "flex", md: "none" },
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "100vh",
            position: "relative",
            backgroundImage: "url('/assets/icons/background.jpg')",
            backgroundSize: "cover",
            backgroundPosition: "center",
            "&::before": {
              content: '""',
              position: "absolute",
              inset: 0,
              background:
                "linear-gradient(180deg, rgba(0,50,20,0.9) 0%, rgba(0,100,40,0.7) 50%, rgba(255,255,255,0.15) 100%)",
              backdropFilter: "blur(3px)",
            },
          }}
        >
          <Box
            sx={{
              position: "relative",
              zIndex: 2,
              textAlign: "center",
              mb: 3,
              mt: 4,
            }}
          >
            <img
              src="/assets/icons/logo.png"
              alt="Restaurant Logo"
              style={{
                maxWidth: 110,
                borderRadius: 20,
                padding: 8,
                boxShadow: "0 6px 24px rgba(255,255,255,0.2)",
                background: "white",
              }}
              draggable={false}
            />

            <Typography
              variant="h6"
              fontWeight={700}
              color="#fff"
              sx={{
                textShadow: "0 2px 6px rgba(0,0,0,0.4)",
                fontFamily: "Montserrat, sans-serif",
                mt: 1,
              }}
            >
              Bienvenido a BCPOS
            </Typography>

            <Typography
              variant="body2"
              color="rgba(255,255,255,0.85)"
              sx={{ maxWidth: 300, mx: "auto", lineHeight: 1.5 }}
            >
              Administra tu restaurante de forma ágil y organizada. Controla
              ventas, productos, inventario, mesas y personal desde un solo
              lugar. Optimiza procesos y mejora la operación diaria de tu
              negocio gastronómico.
            </Typography>
          </Box>

          <Box sx={{ position: "relative", zIndex: 2, width: "100%", p: 3 }}>
            {loginForm}
          </Box>
        </Box>
      ) : (
        <Box
          sx={{
            display: { xs: "none", md: "flex" },
            flex: 1,
            position: "relative",
          }}
        >
          <Box
            sx={{
              width: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "background.paper",
              zIndex: 2,
            }}
          >
            {loginForm}
          </Box>
          <OnboardingLoginAndRegister />
        </Box>
      )}
    </Box>
  );
}
